import React, { useState } from 'react';
import useLocale from '@/hooks/useLocale';
import classNames from 'classnames';

import { Button } from '@/components';
import { Alert } from '@/components/ui/Alert';
import PhoneNumberInput from '@/components/common/EditCommunicationPreferences/PhoneNumberInput';
import { editUser } from '@/lib/api/accountManager/apis/editUser';
import {
  TypeUserPreferenceRecord,
  TypeUserRecord,
  UserPreference,
} from '@/lib/api/accountManager/types';
import Heading, {
  HeadingSize,
  HeadingTag,
} from '@/components/ui/Heading/Heading';

export interface AddPhoneNumberFormProps {
  user: TypeUserRecord;
  header: string;
  subheader?: string;
  subfooter?: string;
  successMessage: string;
  phonePlaceholderLabel: string;
  submitCta: string;
  invalidNumberMessage: string;
  phoneNumber: string | undefined;
  setPhoneNumber: (phoneNumber: string) => void;
  classes?: string;
}

function AddPhoneNumberForm({
  user,
  header,
  subheader,
  subfooter,
  successMessage,
  phonePlaceholderLabel,
  submitCta,
  invalidNumberMessage,
  phoneNumber,
  setPhoneNumber,
  classes,
}: AddPhoneNumberFormProps) {
  const locale = useLocale();
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const [phoneNumberValidationError, setPhoneNumberValidationError] =
    useState<boolean>(false);

  const [isServerError, setIsServerError] = useState(false);

  const clearErrors = () => {
    if (isServerError) {
      setIsServerError(false);
    }

    if (phoneNumberValidationError) {
      setPhoneNumberValidationError(false);
    }
  };

  const handleSubmit = async () => {
    setIsProcessing(true);

    try {
      // when a user adds a phone number, we opt them into both marketing and transactional sms by default
      const response = await editUser({
        userUuid: user.uuid,
        userData: {
          phone_number: phoneNumber,
          user_preferences: [
            {
              preference_name: UserPreference.SmsMarketing,
              preference_value: true,
            },
            {
              preference_name: UserPreference.SmsTransactional,
              preference_value: true,
            },
          ] as TypeUserPreferenceRecord[],
        },
      });

      if (response.ok) {
        setIsSuccess(true);
      } else {
        setIsServerError(true);
      }
    } catch (error) {
      setIsServerError(true);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div
      className={classNames('bg-indigo-pastel p-6', classes)}
      data-testid="phoneNumberForm"
    >
      <Heading
        tag={HeadingTag.H3}
        size={HeadingSize.H3}
        text={header}
        hoverEffect
      />

      {subheader && <p className="text-base pb-6">{subheader}</p>}

      {isSuccess ? (
        <Alert message={successMessage} />
      ) : (
        <>
          <div className="pb-6">
            <PhoneNumberInput
              value={phoneNumber || ''}
              onChange={setPhoneNumber}
              placeholder={phonePlaceholderLabel}
              locale={locale}
              errorMessage={invalidNumberMessage}
              onValidationError={() => {
                setPhoneNumberValidationError(true);
              }}
              onClearError={clearErrors}
            />
          </div>

          <Button
            type="submit"
            ctaText={submitCta}
            onClick={handleSubmit}
            disabled={
              phoneNumberValidationError || isProcessing || !phoneNumber
            }
            includeSpinner={isProcessing}
            fullWidth
          />

          {subfooter && (
            <p className="text-sm text-grey-light mt-4">{subfooter}</p>
          )}
        </>
      )}
    </div>
  );
}

export default AddPhoneNumberForm;
